'use client';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import OriginalDatePicker, { registerLocale } from 'react-datepicker';
import classNames from 'classnames';
import { enGB } from 'date-fns/locale';
import { getIconBaseUrl } from '../../../utils/assets';
import { wrapField } from '../../hooks/useForm';
import ErrorLabel from '../ErrorLabel';
import styles from './dateInput.module.scss';
import TopLabel, { DEFAULT_INPUT_VARIANT, InputVariantEnum } from '../TopLabel';
registerLocale('en-gb', enGB);
const DateInput = React.forwardRef(({ id, name, label, className, minDate, value, onChange, onBlur, error, multiple = false, disabled, filterDate, variant = DEFAULT_INPUT_VARIANT, }, ref) => {
    const hasError = !!error;
    const [focused, setFocused] = useState(false);
    const innerRef = useRef(null);
    const handleOnChange = (value) => {
        if (!multiple) {
            setTimeout(() => {
                var _a, _b;
                if (ref) {
                    (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
                }
                else {
                    (_b = innerRef.current) === null || _b === void 0 ? void 0 : _b.setOpen(false);
                }
            }, 0);
        }
        onChange(value);
    };
    const setNode = (node) => {
        if (ref) {
            ref.current = node;
        }
        else {
            innerRef.current = node;
        }
    };
    const canUseDOM = typeof window !== 'undefined' &&
        typeof window.document !== 'undefined' &&
        // eslint-disable-next-line deprecation/deprecation
        typeof window.document.createElement !== 'undefined';
    const useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
    const inputIdentifier = `bl-haven-dateInput-${id}`;
    useIsomorphicLayoutEffect(() => {
        // Targets DOM element via ID to modify inputmode
        const inputElement = document.querySelector(`#${inputIdentifier}`);
        inputElement === null || inputElement === void 0 ? void 0 : inputElement.setAttribute('inputmode', 'none');
    }, []);
    const placeholder = {
        [InputVariantEnum.DEFAULT]: focused ? undefined : label,
        [InputVariantEnum.LABEL_OUTSIDE]: undefined,
    };
    const hasValue = multiple ? !!value && (value === null || value === void 0 ? void 0 : value.length) > 0 : !!value;
    return (<label htmlFor={inputIdentifier} className={classNames(styles.dateInput, error && styles.error, className, disabled && styles.dateInputDisabled)}>
        {variant === InputVariantEnum.LABEL_OUTSIDE && (<TopLabel className={styles.label} label={label} focused={focused} hasValue={hasValue} disabled={disabled} variant={InputVariantEnum.LABEL_OUTSIDE}/>)}
        <div className={styles.dateInputInner}>
          <OriginalDatePicker id={inputIdentifier} name={name} selected={multiple ? null : value} highlightDates={multiple ? value : undefined} dateFormat={'dd-MM-yyyy'} onChange={(value) => handleOnChange(value)} onBlur={onBlur} minDate={minDate || new Date()} ref={setNode} shouldCloseOnSelect={true} placeholderText={placeholder[variant]} locale={enGB} formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 1)} onFocus={() => setFocused(true)} disabled={disabled} wrapperClassName={styles.datePickerWrapper} calendarClassName={styles.calendar} popperClassName={styles.popper} popperPlacement='bottom' autoComplete='off' popperModifiers={[
            {
                name: 'offset',
                options: {
                    offset: [0, 8],
                },
            },
            { name: 'hide', enabled: false },
            {
                name: 'preventOverflow',
                enabled: false,
            },
        ]} filterDate={filterDate}/>
          <span className={styles.dateInputIcon}>
            <img src={getIconBaseUrl('calendar-blue.svg')} alt='open the calendar'/>
          </span>
          {variant === InputVariantEnum.DEFAULT && (<TopLabel className={styles.label} label={label} focused={focused} hasValue={hasValue} disabled={disabled} variant={InputVariantEnum.DEFAULT}/>)}
          {hasError && <ErrorLabel error={error}/>}
        </div>
      </label>);
});
DateInput.displayName = 'DateInput';
export const DateInputField = wrapField(DateInput);
export default DateInput;
